/**
 * AdminDashboardScreen — Dashboard admin
 *
 * Accessible depuis Profile → ACCÈS RAPIDE (uniquement si user.is_admin)
 * Routes API : /admin/stats, /admin/withdrawals/:id/mark-paid, /admin/withdrawals/:id/reject
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, RefreshControl, SafeAreaView, StatusBar,
  Alert, TextInput, Modal, Platform,
} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import { useAuth } from '../context/AuthContext';
import {
  getAdminStats, markWithdrawalPaid, rejectWithdrawal, triggerCJSync,
  getPushStats, sendTestPush, broadcastPush,
  getAdminClicks, syncAdminClicks,
} from '../services/AdminService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '../context/ThemeContext';

// Palette theme-aware
const getColors = (dark) => ({
  bg:        dark ? '#141414' : '#FAFAFA',
  card:      dark ? '#222222' : '#FFFFFF',
  gold:      '#D4AF37',
  goldDark:  dark ? '#D4B896' : '#B8860B',
  green:     '#2ECC71',
  orange:    '#F39C12',
  red:       '#E53935',
  blue:      dark ? '#7BAEF5' : '#1976D2',
  text:      dark ? '#EEEEEE' : '#1A1A1A',
  textLight: dark ? '#AAAAAA' : '#666',
  textFaint: dark ? '#666666' : '#999',
  border:    dark ? '#383838' : '#EEE',
  inputBg:   dark ? '#2A2A2A' : '#F5F5F5',
});

const PERIODS = [
  { key: '7',   label: '7J'   },
  { key: '30',  label: '30J'  },
  { key: '90',  label: '90J'  },
  { key: 'all', label: 'Tout' },
];

export default function AdminDashboardScreen({ navigation }) {
  const { user } = useAuth();
  const { dark, theme } = useTheme();
  const COLORS = React.useMemo(() => getColors(dark), [dark]);
  const s = React.useMemo(() => makeStyles(COLORS), [COLORS]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays]             = useState('30');

  // Modal pour marquer comme payé
  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [paidTarget, setPaidTarget]       = useState(null);
  const [txRef, setTxRef]                 = useState('');
  const [submitting, setSubmitting]       = useState(false);

  // Sync CJ manuel
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncSecret, setSyncSecret]       = useState('');
  const [syncing, setSyncing]             = useState(false);

  // Notifications push
  const [pushStats, setPushStats]               = useState(null);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [broadcastSegment, setBroadcastSegment] = useState('all');
  const [broadcastTitle, setBroadcastTitle]     = useState('');
  const [broadcastBody, setBroadcastBody]       = useState('');
  // Bloc EN optionnel : si rempli, l'envoi est bilingue (FR aux users locale fr*, EN aux autres)
  const [broadcastTitleEn, setBroadcastTitleEn] = useState('');
  const [broadcastBodyEn, setBroadcastBodyEn]   = useState('');
  const [broadcasting, setBroadcasting]         = useState(false);

  // ═══ Clics CJ détaillés (sync admin) ═══
  const [clicksData, setClicksData]   = useState(null);  // { stats_by_partner, recent_clicks, top_products, ... }
  const [clicksDays, setClicksDays]   = useState(7);
  const [syncingClicks, setSyncingClicks] = useState(false);

  const load = useCallback(async () => {
    try {
      const [data, push, clicks] = await Promise.all([
        getAdminStats(user, days),
        getPushStats(user).catch(() => null),
        getAdminClicks(user, { days: clicksDays, limit: 50 }).catch(() => null),
      ]);
      setStats(data);
      setPushStats(push);
      setClicksData(clicks);
    } catch (e) {
      Alert.alert('Erreur', 'Impossible de charger les stats : ' + e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, days, clicksDays]);

  useEffect(() => { load(); }, [load]);

  // Charger le secret CJ sauvegardé (si déjà saisi une fois)
  useEffect(() => {
    AsyncStorage.getItem('admin_cj_sync_secret').then(s => {
      if (s) setSyncSecret(s);
    });
  }, []);

  const onRefresh = () => { setRefreshing(true); load(); };

  const handleSync = async () => {
    if (!syncSecret.trim()) {
      Alert.alert('Secret requis', 'Entrez le SYNC_SECRET configuré dans le Lambda.');
      return;
    }
    setSyncing(true);
    try {
      const result = await triggerCJSync(syncSecret.trim());
      // Mémoriser le secret pour la prochaine fois
      await AsyncStorage.setItem('admin_cj_sync_secret', syncSecret.trim());
      setSyncModalOpen(false);
      Alert.alert(
        '✓ Sync terminé',
        `Commissions fetchées : ${result.fetched || 0}\n` +
        `Nouvelles : ${result.inserted || 0}\n` +
        `Mises à jour : ${result.updated || 0}\n` +
        `Wallets maj : ${result.wallet_updates || 0}`
      );
      load(); // Recharger les stats
    } catch (e) {
      Alert.alert('Erreur sync', e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSendTestPush = async () => {
    try {
      const result = await sendTestPush(user, {
        title: '✨ Test Outfit',
        body: 'Ceci est une notification test envoyée depuis le Dashboard Admin.',
        screen: 'Home',
      });
      Alert.alert(
        '✓ Envoyée',
        'Notification test envoyée à vous-même. Vérifiez votre téléphone dans quelques secondes.'
      );
    } catch (e) {
      if (e.status === 404) {
        // Pas de token en DB — proposer de re-register puis retry
        Alert.alert(
          'Aucun push token',
          'Tu n\'as pas de push token enregistré. Voulez-vous activer les notifications maintenant ?',
          [
            { text: 'Annuler', style: 'cancel' },
            {
              text: '🔔 Activer',
              onPress: async () => {
                try {
                  // Reset du cache local pour forcer le re-envoi au backend
                  const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
                  await AsyncStorage.removeItem('outfit_push_token_v1');
                  await AsyncStorage.removeItem('outfit_push_token_v1_db_ok');

                  // Re-register
                  const { registerPushNotifications } = await import('../services/PushService');
                  const token = await registerPushNotifications(user?.accessToken);

                  if (!token) {
                    // Diagnostic : récupérer le projectId et les infos device
                    let projectId = null;
                    let platformInfo = '';
                    try {
                      const Constants = require('expo-constants').default;
                      projectId = Constants?.expoConfig?.extra?.eas?.projectId
                               || Constants?.easConfig?.projectId
                               || null;
                      platformInfo = `${Platform.OS} ${Platform.Version || ''}`;
                    } catch {}

                    const details = projectId
                      ? `projectId: ${projectId.slice(0, 8)}... (OK)\nDevice: ${platformInfo}\n\nVérifie que :\n1) Vous avez autorisé les notifications dans les Paramètres Android\n2) Tu utilises un vrai device (pas Expo Go ni émulateur)\n3) L'app a bien été buildée via "eas build" (pas expo start)`
                      : `⚠️ projectId EAS manquant dans app.json !\n\nSolution : dans le dossier outfit-app, exécute :\n\n  eas init\n\nPuis rebuild l'app. Sans ce projectId, les push tokens ne peuvent pas être générés en production.`;

                    Alert.alert('Échec activation push', details);
                    return;
                  }

                  Alert.alert(
                    '✓ Activé',
                    'Push token enregistré avec succès. Clique "Envoyer test" à nouveau.'
                  );
                } catch (err) {
                  console.error('[Admin] re-register failed:', err);
                  Alert.alert('Erreur', err.message || 'Impossible d\'activer les notifications.');
                }
              },
            },
          ]
        );
      } else {
        Alert.alert('Erreur', e.message);
      }
    }
  };

  const handleBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastBody.trim()) {
      Alert.alert('Champs requis', 'Le titre et le message FR sont obligatoires.');
      return;
    }
    // Bilingue si AU MOINS un champ EN est rempli → on recombine avec le
    // séparateur ||EN|| attendu par le backend (FR aux users locale fr*, EN aux autres).
    const SEP = '||EN||';
    const hasEn = !!(broadcastTitleEn.trim() || broadcastBodyEn.trim());
    const titleEn = broadcastTitleEn.trim() || broadcastTitle.trim(); // fallback FR si EN titre vide
    const bodyEn  = broadcastBodyEn.trim()  || broadcastBody.trim();
    const finalTitle = hasEn ? `${broadcastTitle.trim()}${SEP}${titleEn}` : broadcastTitle.trim();
    const finalBody  = hasEn ? `${broadcastBody.trim()}${SEP}${bodyEn}`   : broadcastBody.trim();

    setBroadcasting(true);
    try {
      const result = await broadcastPush(user, broadcastSegment, finalTitle, finalBody, 'Home');
      setBroadcastModalOpen(false);
      setBroadcastTitle(''); setBroadcastBody('');
      setBroadcastTitleEn(''); setBroadcastBodyEn('');
      const counts = result.bilingual
        ? `\nFR : ${result.fr_count || 0} · EN : ${result.en_count || 0}`
        : '';
      Alert.alert(
        '✓ Broadcast envoyé',
        `Envoyé à ${result.target_count || 0} utilisateur(s) (segment : ${broadcastSegment}).${counts}`
      );
    } catch (e) {
      Alert.alert('Erreur', e.message);
    } finally {
      setBroadcasting(false);
    }
  };

  const formatEur = (n) => (Number(n) || 0).toFixed(2).replace('.', ',') + ' €';
  const formatInt = (n) => (Number(n) || 0).toLocaleString('fr-FR');
  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch { return '—'; }
  };
  const daysAgo = (iso) => {
    try {
      const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
      if (diff === 0) return "aujourd'hui";
      if (diff === 1) return 'hier';
      return `il y a ${diff}j`;
    } catch { return ''; }
  };

  const openMarkPaid = (w) => {
    setPaidTarget(w);
    setTxRef('');
    setPaidModalOpen(true);
  };

  const confirmMarkPaid = async () => {
    if (!paidTarget) return;
    setSubmitting(true);
    try {
      await markWithdrawalPaid(user, paidTarget.id, { transactionRef: txRef || null });
      setPaidModalOpen(false);
      Alert.alert('✓ Payé', `Retrait de ${formatEur(paidTarget.amount_eur)} marqué comme payé.`);
      load();
    } catch (e) {
      Alert.alert('Erreur', e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = (w) => {
    Alert.alert(
      'Refuser ce retrait ?',
      `Le montant ${formatEur(w.amount_eur)} sera remis dans le solde disponible du user.`,
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Refuser', style: 'destructive',
          onPress: async () => {
            try {
              await rejectWithdrawal(user, w.id, 'Refusé par admin');
              Alert.alert('✓ Refusé', `${formatEur(w.amount_eur)} remis en solde disponible.`);
              load();
            } catch (e) {
              Alert.alert('Erreur', e.message);
            }
          }
        }
      ]
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={s.loadingBox}>
        <ActivityIndicator size="large" color={COLORS.gold} />
        <Text style={s.loadingText}>Chargement…</Text>
      </SafeAreaView>
    );
  }

  const ov = stats?.overview || {};
  const chart = stats?.daily_chart || [];
  const withdrawals = stats?.pending_withdrawals || [];
  const topProducts = stats?.top_products || [];
  const topUsers = stats?.top_users || [];
  const subs = stats?.subscriptions || {};
  const addons = stats?.addons || {};
  const credit = stats?.credit || {};
  const purchases = stats?.purchases || {};
  const partnerComm = purchases.partner_commissions || {};
  const subsByPlatform = purchases.subscriptions_by_platform || [];
  const addonsByPlatform = purchases.addons_by_platform || [];

  // Libellés réseaux / plateformes
  const NETWORK_LBL = { cj: 'CJ', rakuten: 'Rakuten', awin: 'Awin', skimlinks: 'Skimlinks', tradedoubler: 'Tradedoubler', affiliate_purchase: 'Autres (legacy)', unknown: 'Autres (legacy)' };
  const PLATFORM_LBL = { apple: '🍎 Apple (iOS)', google: '🤖 Google Play', stripe: '💳 Stripe (web)' };

  // Max du graphique pour normaliser les barres
  const maxRevenue = Math.max(1, ...chart.map(c => Number(c.revenue_eur) || 0));

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="dark-content" />

      <View style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.backBtn}>
          <Text style={s.backIcon}>←</Text>
        </TouchableOpacity>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Text style={s.titleIcon}>🔐</Text>
          <Text style={s.title}>Admin</Text>
        </View>
        <TouchableOpacity
          onPress={() => setSyncModalOpen(true)}
          style={s.syncHeaderBtn}
          activeOpacity={0.7}
        >
          <Text style={s.syncHeaderIcon}>🔄</Text>
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={s.content}
        refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.gold} />}>

        {/* Sélecteur période */}
        <View style={s.periodRow}>
          {PERIODS.map(p => (
            <TouchableOpacity key={p.key}
              style={[s.periodBtn, days === p.key && s.periodBtnActive]}
              onPress={() => setDays(p.key)}>
              <Text style={[s.periodLabel, days === p.key && s.periodLabelActive]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Overview stats */}
        <Text style={s.sectionTitle}>VUE D'ENSEMBLE</Text>
        <View style={s.statsGrid}>
          <View style={s.statCard}>
            <Text style={s.statIcon}>🖱️</Text>
            <Text style={s.statValue}>{formatInt(ov.total_clicks)}</Text>
            <Text style={s.statLabel}>Clics</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statIcon}>🛍️</Text>
            <Text style={s.statValue}>{formatInt(ov.total_conversions)}</Text>
            <Text style={s.statLabel}>Achats</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statIcon}>💰</Text>
            <Text style={s.statValue}>{formatEur(ov.total_commissions_eur)}</Text>
            <Text style={s.statLabel}>Commissions</Text>
          </View>
        </View>

        <View style={s.statsGrid}>
          <View style={s.statCard}>
            <Text style={s.statIcon}>👥</Text>
            <Text style={s.statValue}>{formatInt(ov.unique_users)}</Text>
            <Text style={s.statLabel}>Users actifs</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statIcon}>💸</Text>
            <Text style={s.statValue}>{formatEur(ov.total_sales_eur)}</Text>
            <Text style={s.statLabel}>CA total</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statIcon}>📊</Text>
            <Text style={s.statValue}>
              {ov.total_clicks > 0 ? ((ov.total_conversions / ov.total_clicks) * 100).toFixed(1) : 0}%
            </Text>
            <Text style={s.statLabel}>Taux conv.</Text>
          </View>
        </View>

        {/* Graphique revenus */}
        {chart.length > 0 && (
          <>
            <Text style={s.sectionTitle}>REVENUS PAR JOUR</Text>
            <View style={s.chartCard}>
              <View style={s.chartBars}>
                {chart.slice(0, 14).reverse().map((d, i) => {
                  const rev = Number(d.revenue_eur) || 0;
                  const h = Math.max(4, (rev / maxRevenue) * 80);
                  return (
                    <View key={i} style={s.chartBarWrap}>
                      <View style={[s.chartBar, { height: h }]} />
                      <Text style={s.chartBarLabel}>{new Date(d.day).getDate()}</Text>
                    </View>
                  );
                })}
              </View>
              <View style={s.chartFooter}>
                <Text style={s.chartFooterText}>
                  Max : {formatEur(maxRevenue)} · {chart.length} jours avec activité
                </Text>
              </View>
            </View>
          </>
        )}

        {/* ═══════════════ ABONNEMENTS ═══════════════ */}
        <Text style={s.sectionTitle}>💎 ABONNEMENTS</Text>

        {/* MRR / ARR Hero */}
        <View style={s.mrrCard}>
          <View style={{ flex: 1 }}>
            <Text style={s.mrrLabel}>MRR (Revenu mensuel récurrent)</Text>
            <Text style={s.mrrValue}>{formatEur(subs.mrr_eur)}</Text>
            <Text style={s.mrrSub}>ARR : {formatEur(subs.arr_eur)} · {subs.active_count || 0} abonnés actifs</Text>
          </View>
        </View>

        {/* Stats abonnement row */}
        <View style={s.statsGrid}>
          <View style={s.statCard}>
            <Text style={s.statIcon}>✅</Text>
            <Text style={s.statValue}>{formatInt(subs.active_count)}</Text>
            <Text style={s.statLabel}>Actifs</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statIcon}>⏸️</Text>
            <Text style={s.statValue}>{formatInt(subs.cancelled_count)}</Text>
            <Text style={s.statLabel}>Annulés</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statIcon}>🆕</Text>
            <Text style={s.statValue}>{formatInt(subs.new_on_period)}</Text>
            <Text style={s.statLabel}>Nouveaux</Text>
          </View>
        </View>

        {/* Revenus période */}
        <View style={s.revenueBox}>
          <View style={{ flex: 1 }}>
            <Text style={s.revenueLabel}>REVENUS ABONNEMENTS SUR PÉRIODE</Text>
            <Text style={s.revenueValue}>{formatEur(subs.revenue_on_period_eur)}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={s.revenueSubLabel}>Mensuel</Text>
            <Text style={s.revenueSubValue}>{formatEur(subs.mrr_from_monthly_eur)}</Text>
            <Text style={[s.revenueSubLabel, { marginTop: 6 }]}>Annuel (mensualisé)</Text>
            <Text style={s.revenueSubValue}>{formatEur(subs.mrr_from_yearly_eur)}</Text>
          </View>
        </View>

        {/* Répartition par tier */}
        {subs.by_tier && subs.by_tier.length > 0 && (
          <>
            <Text style={s.subSectionTitle}>Par plan d'abonnement</Text>
            <View style={s.listCard}>
              {subs.by_tier.map((row, i) => {
                const tierEmoji = { basic: '⭐', premium: '✨', gold: '👑' }[row.tier] || '📦';
                const tierColor = { basic: '#3B82F6', premium: '#8B5CF6', gold: '#D4AF37' }[row.tier] || '#666';
                return (
                  <View key={i} style={[s.listRow, i !== subs.by_tier.length - 1 && s.listRowBorder]}>
                    <Text style={{ fontSize: 18, width: 30 }}>{tierEmoji}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={[s.listName, { color: tierColor, textTransform: 'uppercase' }]}>{row.tier}</Text>
                      <Text style={s.listSub}>
                        {row.period === 'yearly' ? 'Annuel' : 'Mensuel'} · {row.count} abonné{row.count > 1 ? 's' : ''}
                      </Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text style={s.listValue}>{formatEur(row.total_price_eur)}</Text>
                      <Text style={{ fontSize: 9, color: COLORS.textFaint }}>total</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* Répartition users par tier (tous users inclus free) */}
        {subs.users_by_tier && subs.users_by_tier.length > 0 && (
          <>
            <Text style={s.subSectionTitle}>Distribution de la base users</Text>
            <View style={s.tierBar}>
              {(() => {
                const totalUsers = subs.users_by_tier.reduce((s, r) => s + Number(r.count), 0) || 1;
                const tierColors = { free: '#BDBDBD', basic: '#3B82F6', premium: '#8B5CF6', gold: '#D4AF37' };
                return subs.users_by_tier.map((r, i) => {
                  const pct = (Number(r.count) / totalUsers) * 100;
                  return (
                    <View key={i} style={{
                      width: `${pct}%`,
                      backgroundColor: tierColors[r.tier] || '#ddd',
                      justifyContent: 'center', alignItems: 'center',
                    }}>
                      {pct > 12 && (
                        <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }}>
                          {r.tier.toUpperCase()} {r.count}
                        </Text>
                      )}
                    </View>
                  );
                });
              })()}
            </View>
            <View style={s.tierLegend}>
              {subs.users_by_tier.map((r, i) => {
                const tierColors = { free: '#BDBDBD', basic: '#3B82F6', premium: '#8B5CF6', gold: '#D4AF37' };
                return (
                  <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: tierColors[r.tier] || '#ddd' }} />
                    <Text style={{ fontSize: 10, color: COLORS.textLight }}>
                      {r.tier} · {r.count}
                    </Text>
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* ═══ ADD-ONS (v1.12.959) ═══ */}
        {((addons.consumables && addons.consumables.length > 0) || (addons.subscriptions && addons.subscriptions.length > 0)) && (
          <>
            <Text style={s.sectionTitle}>🛍️ ADD-ONS</Text>
            <View style={s.revenueBox}>
              <View>
                <Text style={s.revenueLabel}>REVENUS ADD-ONS (TOTAL)</Text>
                <Text style={s.revenueValue}>{formatEur(addons.total_revenue_eur)}</Text>
              </View>
              <View>
                <Text style={s.revenueSubLabel}>Sur période</Text>
                <Text style={s.revenueSubValue}>{formatEur(addons.revenue_on_period_eur)}</Text>
                <Text style={[s.revenueSubLabel, { marginTop: 6 }]}>MRR abos add-on</Text>
                <Text style={s.revenueSubValue}>{formatEur(addons.subs_mrr_eur)}</Text>
              </View>
            </View>

            {addons.consumables && addons.consumables.length > 0 && (
              <>
                <Text style={s.subSectionTitle}>Packs (achat unique)</Text>
                <View style={s.listCard}>
                  {addons.consumables.map((r, i) => {
                    const LBL = { addon_tryons_3:'3 Try-Ons', addon_tryons_10:'10 Try-Ons', addon_tryons_25:'25 Try-Ons', addon_scans_50:'Scans IA · pack', addon_scans_200:'Scans IA · gros pack', addon_wardrobe_boost_50:'Boost dressing +50', free_onboarding_tryon:'Try-on offert (onboarding)' };
                    return (
                      <View key={i} style={[s.listRow, i !== addons.consumables.length - 1 && s.listRowBorder]}>
                        <Text style={{ fontSize: 18, width: 30 }}>🛍️</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={s.listName}>{LBL[r.addon_id] || r.addon_id}</Text>
                          <Text style={s.listSub}>{r.total_count} achat{Number(r.total_count) > 1 ? 's' : ''} · {r.active_count} actif{Number(r.active_count) > 1 ? 's' : ''}</Text>
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                          <Text style={s.listValue}>{formatEur(r.total_revenue_eur)}</Text>
                          <Text style={{ fontSize: 9, color: COLORS.textFaint }}>total</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </>
            )}

            {addons.subscriptions && addons.subscriptions.length > 0 && (
              <>
                <Text style={s.subSectionTitle}>Abonnements dressing</Text>
                <View style={s.listCard}>
                  {addons.subscriptions.map((r, i) => {
                    const LBL = { subscription_wardrobe_plus:'Dressing Plus +100', subscription_wardrobe_max:'Dressing Max +250' };
                    return (
                      <View key={i} style={[s.listRow, i !== addons.subscriptions.length - 1 && s.listRowBorder]}>
                        <Text style={{ fontSize: 18, width: 30 }}>👔</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={s.listName}>{LBL[r.addon_id] || r.addon_id}</Text>
                          <Text style={s.listSub}>{r.active_count} actif{Number(r.active_count) > 1 ? 's' : ''} · {r.total_ever} au total</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </>
            )}
          </>
        )}

        {/* Crédit Mytenue (programme fidélité L.521-1 — remplace cashback/retraits) */}
        <View style={s.sectionHeader}>
          <Text style={s.sectionTitle}>💳 CRÉDIT MYTENUE</Text>
        </View>
        <Text style={s.subSectionNote}>
          Programme de fidélité (L.521-1) — utilisable pour abonnements & add-ons, non retirable en cash.
        </Text>

        {/* Bandeau solde global */}
        <View style={s.revenueBox}>
          <Text style={s.revenueLabel}>Crédit actif (utilisable)</Text>
          <Text style={s.revenueValue}>{formatEur(credit.active_eur)}</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
            <View>
              <Text style={s.revenueSubLabel}>En attente (validation partenaire)</Text>
              <Text style={s.revenueSubValue}>{formatEur(credit.pending_eur)}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={s.revenueSubLabel}>Total émis (cumul)</Text>
              <Text style={s.revenueSubValue}>{formatEur(credit.total_earned_eur)}</Text>
            </View>
          </View>
        </View>

        {/* Détail par statut */}
        <View style={s.listCard}>
          <View style={[s.listRow, s.listRowBorder]}>
            <View style={{ flex: 1 }}>
              <Text style={s.listName}>✅ Actif (validé, utilisable)</Text>
              <Text style={s.listSub}>{credit.users_with_active || 0} utilisateur(s) avec solde</Text>
            </View>
            <Text style={s.listValue}>{formatEur(credit.active_eur)}</Text>
          </View>
          <View style={[s.listRow, s.listRowBorder]}>
            <View style={{ flex: 1 }}>
              <Text style={s.listName}>⏳ En attente</Text>
              <Text style={s.listSub}>Crédité, avant validation partenaire (CJ/Rakuten)</Text>
            </View>
            <Text style={[s.listValue, { color: COLORS.orange }]}>{formatEur(credit.pending_eur)}</Text>
          </View>
          <View style={[s.listRow, s.listRowBorder]}>
            <View style={{ flex: 1 }}>
              <Text style={s.listName}>🛒 Utilisé</Text>
              <Text style={s.listSub}>Consommé en abos / add-ons</Text>
            </View>
            <Text style={[s.listValue, { color: COLORS.textLight }]}>{formatEur(credit.used_eur)}</Text>
          </View>
          <View style={s.listRow}>
            <View style={{ flex: 1 }}>
              <Text style={s.listName}>⌛ Expirant ≤ 30j</Text>
              <Text style={s.listSub}>Expiration 12 mois après earning</Text>
            </View>
            <Text style={[s.listValue, { color: credit.expiring_30d_eur > 0 ? COLORS.orange : COLORS.textFaint }]}>
              {formatEur(credit.expiring_30d_eur)}
            </Text>
          </View>
        </View>

        {/* Achats par crédit Mytenue (échanges sans IAP) */}
        <Text style={s.subSectionTitle}>🎁 Achats par crédit (sans IAP)</Text>
        {(() => {
          const redemptions = credit.redemptions || {};
          const byReward = redemptions.by_reward || [];
          const compSubs = redemptions.comp_subscriptions || [];
          const REDEEM_LBL = {
            addon_tryons_3: '3 Try-Ons', addon_tryons_10: '10 Try-Ons', addon_tryons_25: '25 Try-Ons',
            addon_scans_50: '+25 AI scans', addon_scans_200: '+100 AI scans',
            addon_wardrobe_boost_50: 'Boost +50 dressing',
            redeem_sub_basic_1m: 'Basic · 1 mois', redeem_sub_premium_1m: 'Premium · 1 mois', redeem_sub_gold_1m: 'Gold · 1 mois',
          };
          if (!byReward.length && !compSubs.length) {
            return (
              <View style={s.emptyBox}>
                <Text style={s.emptyText}>Aucun achat par crédit pour l'instant</Text>
              </View>
            );
          }
          return (
            <>
              <View style={s.revenueBox}>
                <Text style={s.revenueLabel}>Crédit dépensé en échanges</Text>
                <Text style={s.revenueValue}>{formatEur(redemptions.total_credit_spent_eur)}</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
                  <View>
                    <Text style={s.revenueSubLabel}>Échanges (total)</Text>
                    <Text style={s.revenueSubValue}>{redemptions.total_count || 0}</Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={s.revenueSubLabel}>Sur période</Text>
                    <Text style={s.revenueSubValue}>{redemptions.count_on_period || 0} · {formatEur(redemptions.credit_spent_on_period_eur)}</Text>
                  </View>
                </View>
              </View>
              <View style={s.listCard}>
                {byReward.map((r, i) => (
                  <View key={r.reward_item} style={[s.listRow, i !== byReward.length - 1 && s.listRowBorder]}>
                    <View style={{ flex: 1 }}>
                      <Text style={s.listName}>{REDEEM_LBL[r.reward_item] || r.reward_item}</Text>
                      <Text style={s.listSub}>{r.count} échange(s) · {r.count_on_period || 0} sur période</Text>
                    </View>
                    <Text style={s.listValue}>{formatEur(r.credit_spent_eur)}</Text>
                  </View>
                ))}
              </View>
              {compSubs.length > 0 && (
                <>
                  <Text style={s.subSectionTitle}>Abos offerts par crédit (comp)</Text>
                  <View style={s.listCard}>
                    {compSubs.map((c, i) => (
                      <View key={c.tier} style={[s.listRow, i !== compSubs.length - 1 && s.listRowBorder]}>
                        <View style={{ flex: 1 }}>
                          <Text style={s.listName}>{(c.tier || '').toUpperCase()} · 1 mois</Text>
                          <Text style={s.listSub}>{c.active_count} actif(s) · {c.total_ever} au total</Text>
                        </View>
                        <Text style={s.listValue}>{formatEur(c.credit_spent_eur)}</Text>
                      </View>
                    ))}
                  </View>
                </>
              )}
            </>
          );
        })()}

        {/* ═══ v1.13 — ACHATS RÉELS PROD ═══ */}
        <View style={s.sectionHeader}>
          <Text style={s.sectionTitle}>🛍️ ACHATS RÉELS (PROD)</Text>
        </View>

        {/* Commissions partenaires — tous réseaux */}
        <Text style={s.subSectionTitle}>Commissions partenaires (tous réseaux)</Text>
        <View style={s.revenueBox}>
          <Text style={s.revenueLabel}>Commissions sur période</Text>
          <Text style={s.revenueValue}>{formatEur(partnerComm.commissions_eur)}</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
            <View>
              <Text style={s.revenueSubLabel}>Achats / Ventes</Text>
              <Text style={s.revenueSubValue}>{partnerComm.count || 0} · {formatEur(partnerComm.sales_eur)}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={s.revenueSubLabel}>Crédit accordé</Text>
              <Text style={s.revenueSubValue}>{formatEur(partnerComm.credit_granted_eur)}</Text>
            </View>
          </View>
        </View>

        {/* Par réseau */}
        {(partnerComm.by_network || []).length > 0 && (
          <View style={s.listCard}>
            {(partnerComm.by_network || []).map((n, i, arr) => (
              <View key={n.network} style={[s.listRow, i !== arr.length - 1 && s.listRowBorder]}>
                <View style={{ flex: 1 }}>
                  <Text style={s.listName}>{NETWORK_LBL[n.network] || n.network}</Text>
                  <Text style={s.listSub}>{n.count} achat(s) · ventes {formatEur(n.sales_eur)}</Text>
                </View>
                <Text style={s.listValue}>{formatEur(n.commissions_eur)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Derniers achats partenaires */}
        {(partnerComm.recent || []).length > 0 && (
          <>
            <Text style={s.subSectionTitle}>Derniers achats partenaires</Text>
            <View style={s.listCard}>
              {(partnerComm.recent || []).slice(0, 20).map((r, i, arr) => (
                <View key={i} style={[s.listRow, i !== arr.length - 1 && s.listRowBorder]}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.listName} numberOfLines={1}>
                      {NETWORK_LBL[r.network] || r.network} · {r.user_email || '—'}
                    </Text>
                    <Text style={s.listSub}>
                      {new Date(r.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} · achat {formatEur(r.purchase_eur)} · comm. {formatEur(r.commission_eur)}
                    </Text>
                  </View>
                  <Text style={s.listValue}>+ {formatEur(r.credit_eur)}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* Abonnements par plateforme */}
        <Text style={s.subSectionTitle}>Abonnements par plateforme</Text>
        {subsByPlatform.length === 0 ? (
          <View style={s.emptyBox}><Text style={s.emptyText}>Aucun abonnement</Text></View>
        ) : (
          <View style={s.listCard}>
            {subsByPlatform.map((p, i, arr) => (
              <View key={p.platform} style={[s.listRow, i !== arr.length - 1 && s.listRowBorder]}>
                <View style={{ flex: 1 }}>
                  <Text style={s.listName}>{PLATFORM_LBL[p.platform] || p.platform}</Text>
                  <Text style={s.listSub}>
                    {p.active_count || 0} actif(s) · MRR {formatEur((Number(p.mrr_monthly_eur) || 0) + (Number(p.mrr_yearly_eur) || 0))} · +{p.new_period || 0} période
                  </Text>
                </View>
                <Text style={s.listValue}>{formatEur(p.revenue_period_eur)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Add-ons par plateforme */}
        <Text style={s.subSectionTitle}>Add-ons par plateforme</Text>
        {addonsByPlatform.length === 0 ? (
          <View style={s.emptyBox}><Text style={s.emptyText}>Aucun add-on</Text></View>
        ) : (
          <View style={s.listCard}>
            {addonsByPlatform.map((p, i, arr) => (
              <View key={p.platform} style={[s.listRow, i !== arr.length - 1 && s.listRowBorder]}>
                <View style={{ flex: 1 }}>
                  <Text style={s.listName}>{PLATFORM_LBL[p.platform] || p.platform}</Text>
                  <Text style={s.listSub}>{p.count || 0} achat(s) · {p.count_period || 0} sur période</Text>
                </View>
                <Text style={s.listValue}>{formatEur(p.revenue_period_eur)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* ═══ Section Clics CJ détaillés ═══ */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 24, marginBottom: 4 }}>
          <Text style={[s.sectionTitle, { marginTop: 0, marginBottom: 0 }]}>🖱️ CLICS PARTENAIRES CJ</Text>
          <TouchableOpacity
            onPress={async () => {
              if (syncingClicks) return;
              setSyncingClicks(true);
              try {
                const result = await syncAdminClicks(user);
                Alert.alert(
                  '✓ Synchronisation OK',
                  `Total : ${result?.counts?.total_clicks || 0} clics\n` +
                  `24h : ${result?.counts?.clicks_24h || 0}\n` +
                  `7j : ${result?.counts?.clicks_7d || 0}\n` +
                  `Conversions : ${result?.matching?.conversions_total || 0}\n` +
                  `Wallets recalculés : ${result?.wallets_recalculated || 0}`,
                );
                load(); // refresh
              } catch (e) {
                Alert.alert('Erreur', 'Sync échouée : ' + e.message);
              } finally {
                setSyncingClicks(false);
              }
            }}
            disabled={syncingClicks}
            style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, backgroundColor: COLORS.gold }}
            activeOpacity={0.8}
          >
            {syncingClicks ? (
              <ActivityIndicator size="small" color={theme.text} />
            ) : (
              <Text style={{ fontSize: 11, fontWeight: '700', color: '#0E0C0A', letterSpacing: 0.5 }}>
                ↻ SYNC
              </Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Sélecteur période clics */}
        <View style={{ flexDirection: 'row', gap: 6, marginBottom: 12 }}>
          {[1, 7, 30].map(d => (
            <TouchableOpacity
              key={d}
              onPress={() => setClicksDays(d)}
              style={{
                paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6,
                backgroundColor: clicksDays === d ? COLORS.gold : COLORS.card,
                borderWidth: 1, borderColor: clicksDays === d ? COLORS.gold : COLORS.border,
              }}
              activeOpacity={0.8}
            >
              <Text style={{
                fontSize: 11, fontWeight: '600',
                color: clicksDays === d ? '#0E0C0A' : COLORS.text,
              }}>
                {d === 1 ? '24h' : d === 7 ? '7 jours' : '30 jours'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Stats par partner */}
        {!clicksData ? (
          <View style={s.emptyBox}><Text style={s.emptyText}>Chargement…</Text></View>
        ) : (clicksData.stats_by_partner || []).length === 0 ? (
          <View style={s.emptyBox}><Text style={s.emptyText}>Aucun clic sur la période</Text></View>
        ) : (
          <View style={s.listCard}>
            {clicksData.stats_by_partner.map((p, i) => (
              <View key={i} style={[s.listRow, i !== clicksData.stats_by_partner.length - 1 && s.listRowBorder]}>
                <View style={{ flex: 1 }}>
                  <Text style={s.listName}>
                    {p.partner === 'iqueens' ? 'iQueens'
                      : p.partner === 'cashmere' ? 'Cashmere Boutique'
                      : (p.partner || 'unknown').toUpperCase()}
                  </Text>
                  <Text style={s.listSub}>
                    {p.unique_users} user{p.unique_users > 1 ? 's' : ''} · {p.unique_sessions} session{p.unique_sessions > 1 ? 's' : ''}
                  </Text>
                </View>
                <Text style={s.listValue}>
                  {p.clicks}<Text style={{ fontSize: 10, color: COLORS.textLight }}> clics</Text>
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Clics récents (10 dernières lignes) */}
        {clicksData && (clicksData.recent_clicks || []).length > 0 && (
          <>
            <Text style={[s.sectionTitle, { fontSize: 13, marginTop: 16 }]}>📋 DERNIERS CLICS</Text>
            <View style={s.listCard}>
              {clicksData.recent_clicks.slice(0, 10).map((c, i) => {
                const dateStr = c.clicked_at
                  ? new Date(c.clicked_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                  : '—';
                return (
                  <View key={c.id || i} style={[s.listRow, i !== 9 && s.listRowBorder]}>
                    <View style={{ flex: 1 }}>
                      <Text style={s.listName} numberOfLines={1}>
                        {c.product_name || '—'}
                      </Text>
                      <Text style={s.listSub}>
                        {(c.partner || 'unknown').toUpperCase()} · {c.user_email?.split('@')[0] || c.cognito_sub?.slice(0, 8) || 'guest'} · {dateStr}
                      </Text>
                    </View>
                    {c.converted > 0 ? (
                      <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, backgroundColor: COLORS.green }}>
                        <Text style={{ fontSize: 9, fontWeight: '700', color: '#FFF' }}>VENDU</Text>
                      </View>
                    ) : (
                      <Text style={{ fontSize: 11, color: COLORS.textLight }}>~{formatEur(c.price)}</Text>
                    )}
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* Top produits */}
        <Text style={s.sectionTitle}>🏆 TOP 10 PRODUITS</Text>
        {topProducts.length === 0 ? (
          <View style={s.emptyBox}><Text style={s.emptyText}>Aucun clic sur cette période</Text></View>
        ) : (
          <View style={s.listCard}>
            {topProducts.map((p, i) => (
              <View key={i} style={[s.listRow, i !== topProducts.length - 1 && s.listRowBorder]}>
                <Text style={s.listRank}>{i + 1}.</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.listName} numberOfLines={2}>{p.product_name}</Text>
                  <Text style={s.listSub}>{p.partner?.toUpperCase()} · ~{formatEur(p.avg_price)}</Text>
                </View>
                <Text style={s.listValue}>{p.clicks}<Text style={{ fontSize: 10, color: COLORS.textLight }}> clics</Text></Text>
              </View>
            ))}
          </View>
        )}

        {/* Top users */}
        <Text style={s.sectionTitle}>👥 TOP 10 UTILISATEURS</Text>
        {topUsers.length === 0 ? (
          <View style={s.emptyBox}><Text style={s.emptyText}>Aucun utilisateur actif</Text></View>
        ) : (
          <View style={s.listCard}>
            {topUsers.map((u, i) => (
              <View key={i} style={[s.listRow, i !== topUsers.length - 1 && s.listRowBorder]}>
                <Text style={s.listRank}>{i + 1}.</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.listName} numberOfLines={1}>
                    {u.user_email || u.cognito_sub?.slice(0, 12)}
                  </Text>
                  <Text style={s.listSub}>
                    {(u.user_tier || 'free').toUpperCase()} · {u.total_clicks} clics · {u.total_conversions} achats
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={s.listValue}>{formatEur(u.total_commissions_eur)}</Text>
                  <Text style={{ fontSize: 9, color: COLORS.green, fontWeight: '700' }}>
                    crédit {formatEur(u.credit_active_eur != null ? u.credit_active_eur : u.total_cashback_eur)}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* ═══════════════ NOTIFICATIONS PUSH ═══════════════ */}
        <Text style={s.sectionTitle}>🔔 NOTIFICATIONS PUSH</Text>
        <View style={s.pushCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <View>
              <Text style={s.pushStatLabel}>Users avec push activé</Text>
              <Text style={s.pushStatValue}>
                {pushStats?.users_with_push || 0}
                <Text style={s.pushStatSub}> / {pushStats?.total_users || 0}</Text>
              </Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={s.pushStatLabel}>Opt-in rate</Text>
              <Text style={s.pushStatValue}>
                {pushStats?.total_users > 0
                  ? ((pushStats.users_with_push / pushStats.total_users) * 100).toFixed(0)
                  : 0}%
              </Text>
            </View>
          </View>

          <View style={s.pushTierRow}>
            {['free', 'basic', 'premium', 'gold'].map(tier => {
              const count = Number(pushStats?.[`${tier}_with_push`]) || 0;
              const tierColors = { free: '#BDBDBD', basic: '#3B82F6', premium: '#8B5CF6', gold: '#D4AF37' };
              return (
                <View key={tier} style={s.pushTierItem}>
                  <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: tierColors[tier] }} />
                  <Text style={s.pushTierText}>
                    {tier.toUpperCase()}: <Text style={{ fontWeight: '700' }}>{count}</Text>
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={s.pushActionsRow}>
            <TouchableOpacity style={s.pushBtn} onPress={handleSendTestPush}>
              <Text style={s.pushBtnIcon}>📨</Text>
              <Text style={s.pushBtnLabel}>Test (moi)</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.pushBtnPrimary} onPress={() => setBroadcastModalOpen(true)}>
              <Text style={s.pushBtnIcon}>📢</Text>
              <Text style={s.pushBtnPrimaryLabel}>Broadcast</Text>
            </TouchableOpacity>
          </View>

          {/* ═══ Liste des users avec push activé (diagnostic) ═══ */}
          {Array.isArray(pushStats?.users) && pushStats.users.length > 0 && (
            <View style={{ marginTop: 14, paddingTop: 14, borderTopWidth: 1, borderTopColor: '#EEE' }}>
              <Text style={{ fontSize: 11, color: '#666', letterSpacing: 1, marginBottom: 8 }}>
                UTILISATEURS AVEC PUSH ({pushStats.users.length})
              </Text>
              {pushStats.users.slice(0, 20).map((u, i) => (
                <View key={u.user_id + i} style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: 6,
                  borderBottomWidth: i < pushStats.users.length - 1 ? 1 : 0,
                  borderBottomColor: '#F5F5F5',
                }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1A1A1A' }} numberOfLines={1}>
                      {u.first_name || '(sans prénom)'} · {u.email || '(sans email)'}
                    </Text>
                    <Text style={{ fontSize: 10, color: '#999' }} numberOfLines={1}>
                      {u.user_id} · {u.push_preview}
                    </Text>
                  </View>
                  <View style={{
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    borderRadius: 4,
                    backgroundColor: u.tier === 'free' ? '#F5F5F5' : u.tier === 'basic' ? '#DBEAFE' : u.tier === 'premium' ? '#EDE9FE' : '#FEF3C7',
                  }}>
                    <Text style={{ fontSize: 9, fontWeight: '600', color: '#555' }}>{(u.tier || 'free').toUpperCase()}</Text>
                  </View>
                </View>
              ))}
              {pushStats.users.length > 20 && (
                <Text style={{ fontSize: 11, color: '#999', marginTop: 6, textAlign: 'center' }}>
                  ... et {pushStats.users.length - 20} autres
                </Text>
              )}
            </View>
          )}
        </View>

        {/* Infos fin de page */}
        <View style={s.infoBox}>
          <Text style={s.infoText}>
            Crédit Mytenue actif : {formatEur(credit.active_eur)}{'\n'}
            En attente (validation) : {formatEur(credit.pending_eur)} · Total émis : {formatEur(credit.total_earned_eur)}
          </Text>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Modal mark-paid */}
      <Modal visible={paidModalOpen} transparent animationType="fade" onRequestClose={() => setPaidModalOpen(false)}>
        <View style={s.modalOverlay}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>Marquer comme payé</Text>
            <Text style={s.modalSub}>
              Confirmation du transfert PayPal{paidTarget ? ` de ${formatEur(paidTarget.amount_eur)}` : ''}
              {paidTarget ? ` vers ${paidTarget.paypal_email}` : ''}
            </Text>
            <Text style={s.modalLabel}>RÉFÉRENCE TRANSACTION (OPTIONNEL)</Text>
            <TextInput style={s.modalInput} value={txRef} onChangeText={setTxRef}
              placeholder="Ex: PP-1A2B3C..." placeholderTextColor={COLORS.textFaint} />
            <View style={s.modalActions}>
              <TouchableOpacity style={s.modalBtnCancel} onPress={() => setPaidModalOpen(false)}>
                <Text style={s.modalBtnCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.modalBtnConfirm, submitting && { opacity: 0.5 }]}
                onPress={confirmMarkPaid} disabled={submitting}>
                {submitting ? <ActivityIndicator color="#fff" /> : <Text style={s.modalBtnConfirmText}>Confirmer</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Modal Sync CJ manuel */}
      <Modal visible={syncModalOpen} transparent animationType="fade" onRequestClose={() => setSyncModalOpen(false)}>
        <View style={s.modalOverlay}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>🔄 Sync CJ manuel</Text>
            <Text style={s.modalSub}>
              Fetch immédiat des commissions depuis l'API CJ (sans attendre le cron 3h).
            </Text>
            <Text style={s.modalLabel}>SYNC_SECRET</Text>
            <TextInput
              style={s.modalInput}
              value={syncSecret}
              onChangeText={setSyncSecret}
              placeholder="Variable d'env Lambda SYNC_SECRET"
              placeholderTextColor={COLORS.textFaint}
              autoCapitalize="none"
              autoCorrect={false}
              secureTextEntry
            />
            <Text style={s.modalHint}>Le secret est mémorisé localement après la première fois.</Text>
            <View style={s.modalActions}>
              <TouchableOpacity style={s.modalBtnCancel} onPress={() => setSyncModalOpen(false)}>
                <Text style={s.modalBtnCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.modalBtnConfirm, syncing && { opacity: 0.5 }]}
                onPress={handleSync}
                disabled={syncing}
              >
                {syncing
                  ? <ActivityIndicator color="#fff" />
                  : <Text style={s.modalBtnConfirmText}>Lancer sync</Text>
                }
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Modal Broadcast Push */}
      <Modal visible={broadcastModalOpen} transparent animationType="fade" onRequestClose={() => setBroadcastModalOpen(false)}>
        <View style={s.modalOverlay}>
          <View style={[s.modalCard, { maxWidth: 420 }]}>
            <Text style={s.modalTitle}>📢 Broadcast notification</Text>
            <Text style={s.modalSub}>
              Envoie une notification push à tous les users d'un segment.
            </Text>

            <Text style={s.modalLabel}>SEGMENT</Text>
            <View style={s.segmentRow}>
              {['all', 'free', 'basic', 'premium', 'gold'].map(seg => (
                <TouchableOpacity
                  key={seg}
                  style={[s.segmentBtn, broadcastSegment === seg && s.segmentBtnActive]}
                  onPress={() => setBroadcastSegment(seg)}
                >
                  <Text style={[s.segmentLabel, broadcastSegment === seg && s.segmentLabelActive]}>
                    {seg.toUpperCase()}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* ─── Bloc FR (obligatoire) ─── */}
            <Text style={s.modalLabel}>🇫🇷 TITRE (FR)</Text>
            <TextInput
              style={s.modalInput}
              value={broadcastTitle}
              onChangeText={setBroadcastTitle}
              placeholder="✨ Vivez l'expérience Mytenue"
              placeholderTextColor={COLORS.textFaint}
              maxLength={80}
            />

            <Text style={s.modalLabel}>🇫🇷 MESSAGE (FR)</Text>
            <TextInput
              style={[s.modalInput, { minHeight: 80, textAlignVertical: 'top' }]}
              value={broadcastBody}
              onChangeText={setBroadcastBody}
              placeholder="Ajoutez vos achats, générez vos looks par IA, testez le try-on gratuit…"
              placeholderTextColor={COLORS.textFaint}
              multiline
              maxLength={220}
            />
            <Text style={s.modalHint}>{broadcastBody.length}/220 caractères</Text>

            {/* ─── Bloc EN (optionnel → active l'envoi bilingue auto) ─── */}
            <Text style={s.modalLabel}>🇬🇧 TITLE (EN) — optionnel</Text>
            <TextInput
              style={s.modalInput}
              value={broadcastTitleEn}
              onChangeText={setBroadcastTitleEn}
              placeholder="✨ Live the Mytenue experience"
              placeholderTextColor={COLORS.textFaint}
              maxLength={80}
            />

            <Text style={s.modalLabel}>🇬🇧 MESSAGE (EN) — optionnel</Text>
            <TextInput
              style={[s.modalInput, { minHeight: 80, textAlignVertical: 'top' }]}
              value={broadcastBodyEn}
              onChangeText={setBroadcastBodyEn}
              placeholder="Add your purchases, generate AI looks, try the free try-on…"
              placeholderTextColor={COLORS.textFaint}
              multiline
              maxLength={220}
            />
            <Text style={s.modalHint}>
              {broadcastBodyEn.length}/220 · {(broadcastTitleEn.trim() || broadcastBodyEn.trim())
                ? 'Bilingue : FR aux users 🇫🇷, EN aux autres'
                : 'EN vide → FR envoyé à tout le monde'}
            </Text>

            <View style={s.modalActions}>
              <TouchableOpacity style={s.modalBtnCancel} onPress={() => setBroadcastModalOpen(false)}>
                <Text style={s.modalBtnCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.modalBtnConfirm, broadcasting && { opacity: 0.5 }]}
                onPress={handleBroadcast}
                disabled={broadcasting}
              >
                {broadcasting
                  ? <ActivityIndicator color="#fff" />
                  : <Text style={s.modalBtnConfirmText}>Envoyer</Text>
                }
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const makeStyles = (COLORS) => StyleSheet.create({
  safe: { flex: 1, backgroundColor: COLORS.bg },
  loadingBox: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, backgroundColor: COLORS.bg },
  loadingText: { color: COLORS.textLight, fontSize: 13 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: COLORS.border },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backIcon: { fontSize: 22, color: COLORS.text },
  titleIcon: { fontSize: 14 },
  title: { fontSize: 17, fontWeight: '600', color: COLORS.text },
  syncHeaderBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 20, backgroundColor: '#F5F5F5' },
  syncHeaderIcon: { fontSize: 18 },

  content: { padding: 16, gap: 16 },

  periodRow: { flexDirection: 'row', backgroundColor: COLORS.card, borderRadius: 10,
    padding: 4, borderWidth: 1, borderColor: COLORS.border },
  periodBtn: { flex: 1, paddingVertical: 8, alignItems: 'center', borderRadius: 6 },
  periodBtnActive: { backgroundColor: '#1A1A1A' },
  periodLabel: { fontSize: 12, fontWeight: '600', color: COLORS.textLight, letterSpacing: 0.5 },
  periodLabelActive: { color: COLORS.gold },

  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: -4 },
  sectionTitle: { fontSize: 10, letterSpacing: 2, color: COLORS.textLight, fontWeight: '700', marginTop: 8, marginBottom: -4 },
  countBadge: { backgroundColor: COLORS.red, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, minWidth: 20, alignItems: 'center' },
  countBadgeText: { fontSize: 10, fontWeight: '800', color: '#fff' },

  statsGrid: { flexDirection: 'row', gap: 8 },
  statCard: { flex: 1, backgroundColor: COLORS.card, borderRadius: 10, padding: 10,
    alignItems: 'center', borderWidth: 1, borderColor: COLORS.border, gap: 2 },
  statIcon: { fontSize: 16 },
  statValue: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  statLabel: { fontSize: 9, color: COLORS.textLight, letterSpacing: 0.5 },

  chartCard: { backgroundColor: COLORS.card, borderRadius: 12, padding: 12,
    borderWidth: 1, borderColor: COLORS.border },
  chartBars: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between',
    height: 100, gap: 4 },
  chartBarWrap: { flex: 1, alignItems: 'center', gap: 3 },
  chartBar: { width: '80%', backgroundColor: COLORS.gold, borderTopLeftRadius: 3, borderTopRightRadius: 3 },
  chartBarLabel: { fontSize: 9, color: COLORS.textFaint },
  chartFooter: { marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: COLORS.border },
  chartFooterText: { fontSize: 10, color: COLORS.textLight, textAlign: 'center' },

  // Abonnements
  mrrCard: { backgroundColor: '#1A1A1A', borderRadius: 12, padding: 16, borderWidth: 1.5, borderColor: COLORS.gold, gap: 4 },
  mrrLabel: { fontSize: 10, letterSpacing: 2, color: COLORS.gold, fontWeight: '700' },
  mrrValue: { fontSize: 32, fontWeight: '300', color: '#fff', marginTop: 4 },
  mrrSub: { fontSize: 11, color: '#999', marginTop: 4 },
  revenueBox: { flexDirection: 'row', backgroundColor: COLORS.card, borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: COLORS.border, gap: 12 },
  revenueLabel: { fontSize: 9, letterSpacing: 1.5, color: COLORS.textLight, fontWeight: '700' },
  revenueValue: { fontSize: 22, fontWeight: '700', color: COLORS.green, marginTop: 6 },
  revenueSubLabel: { fontSize: 9, letterSpacing: 0.5, color: COLORS.textFaint, fontWeight: '600' },
  revenueSubValue: { fontSize: 12, fontWeight: '700', color: COLORS.text, marginTop: 2 },
  subSectionTitle: { fontSize: 12, fontWeight: '600', color: COLORS.textLight, marginTop: 8, marginBottom: -4 },
  subSectionNote: { fontSize: 11, color: COLORS.textFaint, lineHeight: 15, marginTop: -2, marginBottom: 10 },
  tierBar: { flexDirection: 'row', height: 32, borderRadius: 8, overflow: 'hidden',
    backgroundColor: COLORS.border, marginTop: 8 },
  tierLegend: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 4 },

  // Push notifications
  pushCard: { backgroundColor: COLORS.card, borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: COLORS.border },
  pushStatLabel: { fontSize: 9, letterSpacing: 1.5, color: COLORS.textLight, fontWeight: '700' },
  pushStatValue: { fontSize: 22, fontWeight: '700', color: COLORS.text, marginTop: 4 },
  pushStatSub: { fontSize: 13, fontWeight: '400', color: COLORS.textFaint },
  pushTierRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, paddingVertical: 10,
    borderTopWidth: 1, borderTopColor: COLORS.border, marginTop: 4 },
  pushTierItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  pushTierText: { fontSize: 10, color: COLORS.textLight },
  pushActionsRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  pushBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 10, borderRadius: 8, backgroundColor: '#F5F5F5',
    borderWidth: 1, borderColor: COLORS.border },
  pushBtnPrimary: { flex: 1.2, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 10, borderRadius: 8, backgroundColor: '#1A1A1A',
    borderWidth: 1, borderColor: COLORS.gold },
  pushBtnIcon: { fontSize: 14 },
  pushBtnLabel: { fontSize: 12, fontWeight: '600', color: COLORS.text },
  pushBtnPrimaryLabel: { fontSize: 12, fontWeight: '700', color: COLORS.gold, letterSpacing: 0.3 },

  // Broadcast segment selector
  segmentRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  segmentBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6,
    backgroundColor: '#F5F5F5', borderWidth: 1, borderColor: COLORS.border },
  segmentBtnActive: { backgroundColor: '#1A1A1A', borderColor: COLORS.gold },
  segmentLabel: { fontSize: 10, fontWeight: '700', color: COLORS.textLight, letterSpacing: 0.5 },
  segmentLabelActive: { color: COLORS.gold },

  withdrawalCard: { backgroundColor: COLORS.card, borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: '#FFE0B2', gap: 10 },
  withdrawalUser: { fontSize: 14, fontWeight: '600', color: COLORS.text },
  withdrawalEmail: { fontSize: 11, color: COLORS.textLight },
  withdrawalDate: { fontSize: 10, color: COLORS.textFaint },
  withdrawalAmount: { fontSize: 18, fontWeight: '800', color: COLORS.orange },
  withdrawalActions: { flexDirection: 'row', gap: 8 },
  btnReject: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center',
    backgroundColor: '#FFEBEE', borderWidth: 1, borderColor: '#FFCDD2' },
  btnRejectText: { fontSize: 12, fontWeight: '700', color: COLORS.red, letterSpacing: 0.3 },
  btnPaid: { flex: 1.4, paddingVertical: 10, borderRadius: 8, alignItems: 'center',
    backgroundColor: COLORS.green },
  btnPaidText: { fontSize: 12, fontWeight: '800', color: '#fff', letterSpacing: 0.3 },

  listCard: { backgroundColor: COLORS.card, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border, overflow: 'hidden' },
  listRow: { flexDirection: 'row', alignItems: 'center', padding: 12, gap: 10 },
  listRowBorder: { borderBottomWidth: 1, borderBottomColor: COLORS.border },
  listRank: { fontSize: 13, fontWeight: '700', color: COLORS.goldDark, width: 24 },
  listName: { fontSize: 13, fontWeight: '500', color: COLORS.text },
  listSub: { fontSize: 10, color: COLORS.textLight, marginTop: 2, letterSpacing: 0.3 },
  listValue: { fontSize: 14, fontWeight: '700', color: COLORS.text },

  emptyBox: { backgroundColor: COLORS.card, borderRadius: 10, padding: 20, alignItems: 'center',
    borderWidth: 1, borderColor: COLORS.border, gap: 4 },
  emptyIcon: { fontSize: 24, color: COLORS.green },
  emptyText: { fontSize: 12, color: COLORS.textLight },

  infoBox: { backgroundColor: '#F5F5F5', borderRadius: 10, padding: 12, borderWidth: 1, borderColor: COLORS.border },
  infoText: { fontSize: 11, color: COLORS.textLight, lineHeight: 16 },

  modalOverlay: { flex: 1, backgroundColor: 'transparent', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modalCard: { backgroundColor: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 400, gap: 10 },
  modalTitle: { fontSize: 18, fontWeight: '700', color: COLORS.text },
  modalSub: { fontSize: 12, color: COLORS.textLight, marginBottom: 8 },
  modalLabel: { fontSize: 9, letterSpacing: 1.5, color: COLORS.textLight, fontWeight: '700', marginTop: 8 },
  modalInput: { backgroundColor: '#F5F5F5', borderRadius: 8, padding: 12, fontSize: 14, color: COLORS.text, borderWidth: 1, borderColor: COLORS.border },
  modalHint: { fontSize: 11, color: COLORS.textFaint, marginTop: -4 },
  modalActions: { flexDirection: 'row', gap: 10, marginTop: 16 },
  modalBtnCancel: { flex: 1, backgroundColor: '#F5F5F5', paddingVertical: 14, borderRadius: 8, alignItems: 'center' },
  modalBtnCancelText: { color: COLORS.textLight, fontSize: 13, fontWeight: '600' },
  modalBtnConfirm: { flex: 1.5, backgroundColor: COLORS.green, paddingVertical: 14, borderRadius: 8, alignItems: 'center' },
  modalBtnConfirmText: { color: '#fff', fontSize: 13, fontWeight: '800', letterSpacing: 0.8 },
});
